import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Square, ArrowRight, Loader2, RotateCcw, Waves } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_SECONDS = 30;

// ---------- localized copy ---------- //
const COPY = {
  de: {
    badge: "NEU · VOICE-ANFRAGE",
    overline: "— In 20 Sekunden zum Angebot",
    title_1: "Sprechen Sie",
    title_2: "Ihre Anfrage.",
    intro:
      "Kein Formular. Kein Tippen. Drücken Sie den Knopf, sprechen Sie 20 Sekunden über Ihr Projekt — wir transkribieren automatisch und melden uns.",
    cta_start: "Aufnahme starten",
    cta_stop: "Aufnahme stoppen",
    cta_reset: "Neu aufnehmen",
    cta_send: "Anfrage senden",
    hint_permission: "Ihr Browser fragt nach Mikrofon-Zugriff.",
    hint_max: `Max. ${MAX_SECONDS} Sekunden`,
    transcribing: "Wird transkribiert …",
    transcript_title: "Transkript",
    transcript_edit_hint: "Sie können den Text vor dem Senden noch korrigieren.",
    err_perm: "Mikrofon-Zugriff verweigert. Bitte im Browser erlauben.",
    err_no_mic: "Kein Mikrofon gefunden.",
    err_short: "Aufnahme zu kurz — bitte mindestens 3 Sekunden sprechen.",
    err_generic: "Aufnahme fehlgeschlagen.",
    toast_sent: "Anfrage übermittelt — wir melden uns innerhalb eines Werktages.",
    example_prompts: [
      "«Ich habe 500 Stahlbolzen, brauche Korrosionsschutz für Aussenanwendung, Termin in 2 Wochen — was empfehlen Sie?»",
      "«Wir produzieren Sensorgehäuse aus 1.4301, brauchen Elektropolieren für den Medizinbereich.»",
      "«Grossserie 5'000 Kleinteile, Trommelverzinken möglich? Preis pro 1'000 Stück?»",
    ],
    example_title: "Beispiel-Anfragen",
    disclaimer:
      "Voice-Anfragen werden automatisch transkribiert (OpenAI Whisper) und an unser Sekretariat gesendet. Audio wird nach der Verarbeitung nicht dauerhaft gespeichert.",
  },
  en: {
    badge: "NEW · VOICE ENQUIRY",
    overline: "— From voice to quote in 20 seconds",
    title_1: "Speak",
    title_2: "your enquiry.",
    intro:
      "No form. No typing. Hit the button, talk 20 seconds about your project — we transcribe automatically and get back to you.",
    cta_start: "Start recording",
    cta_stop: "Stop recording",
    cta_reset: "Record again",
    cta_send: "Send enquiry",
    hint_permission: "Your browser will ask for microphone access.",
    hint_max: `Max. ${MAX_SECONDS} seconds`,
    transcribing: "Transcribing …",
    transcript_title: "Transcript",
    transcript_edit_hint: "You can edit the text before sending.",
    err_perm: "Microphone access denied. Please allow it in your browser.",
    err_no_mic: "No microphone found.",
    err_short: "Recording too short — please speak at least 3 seconds.",
    err_generic: "Recording failed.",
    toast_sent: "Enquiry submitted — we'll be in touch within one business day.",
    example_prompts: [
      "\"I have 500 steel bolts, need outdoor corrosion protection, delivery in 2 weeks — what do you recommend?\"",
      "\"We make sensor housings in 1.4301 stainless, need electropolishing for medical use.\"",
      "\"Large series 5,000 small parts, is barrel plating possible? Price per 1,000?\"",
    ],
    example_title: "Sample enquiries",
    disclaimer:
      "Voice enquiries are transcribed automatically (OpenAI Whisper) and sent to our office. Audio is not stored after processing.",
  },
  fr: {
    badge: "NOUVEAU · DEMANDE VOCALE",
    overline: "— De la voix au devis en 20 secondes",
    title_1: "Dictez",
    title_2: "votre demande.",
    intro:
      "Pas de formulaire. Pas de saisie. Appuyez, parlez 20 secondes de votre projet — nous transcrivons automatiquement et vous recontactons.",
    cta_start: "Démarrer l'enregistrement",
    cta_stop: "Arrêter",
    cta_reset: "Réenregistrer",
    cta_send: "Envoyer la demande",
    hint_permission: "Votre navigateur demandera l'accès au micro.",
    hint_max: `Max. ${MAX_SECONDS} secondes`,
    transcribing: "Transcription …",
    transcript_title: "Transcription",
    transcript_edit_hint: "Vous pouvez modifier le texte avant l'envoi.",
    err_perm: "Accès au micro refusé. Autorisez-le dans le navigateur.",
    err_no_mic: "Aucun micro détecté.",
    err_short: "Enregistrement trop court — parlez au moins 3 secondes.",
    err_generic: "Échec de l'enregistrement.",
    toast_sent: "Demande envoyée — nous vous recontactons sous un jour ouvré.",
    example_prompts: [
      "« J'ai 500 boulons acier, besoin d'une protection extérieure, livraison sous 2 semaines — que recommandez-vous ? »",
      "« Nous fabriquons des boîtiers de capteur en 1.4301, besoin d'électropolissage médical. »",
      "« Grande série 5'000 petites pièces, tonneau possible ? Prix pour 1'000 ? »",
    ],
    example_title: "Exemples de demandes",
    disclaimer:
      "Les demandes vocales sont transcrites automatiquement (OpenAI Whisper) et envoyées à notre bureau. L'audio n'est pas conservé après traitement.",
  },
  it: {
    badge: "NUOVO · RICHIESTA VOCALE",
    overline: "— Dalla voce all'offerta in 20 secondi",
    title_1: "Detti",
    title_2: "la Sua richiesta.",
    intro:
      "Niente moduli. Niente digitare. Prema il pulsante, parli 20 secondi del Suo progetto — trascriviamo automaticamente e La ricontattiamo.",
    cta_start: "Avvia registrazione",
    cta_stop: "Ferma",
    cta_reset: "Registra di nuovo",
    cta_send: "Invia richiesta",
    hint_permission: "Il browser chiederà l'accesso al microfono.",
    hint_max: `Max. ${MAX_SECONDS} secondi`,
    transcribing: "Trascrizione …",
    transcript_title: "Trascrizione",
    transcript_edit_hint: "Può modificare il testo prima dell'invio.",
    err_perm: "Accesso al microfono negato. Consentirlo nel browser.",
    err_no_mic: "Nessun microfono rilevato.",
    err_short: "Registrazione troppo breve — parli almeno 3 secondi.",
    err_generic: "Registrazione fallita.",
    toast_sent: "Richiesta inviata — La ricontattiamo entro un giorno lavorativo.",
    example_prompts: [
      "« Ho 500 bulloni in acciaio, serve protezione anticorrosione esterna, consegna in 2 settimane — cosa consigliate? »",
      "« Produciamo custodie sensori in 1.4301, serve elettropolitura per uso medicale. »",
      "« Grande serie 5'000 pezzi piccoli, zincatura a tamburo possibile? Prezzo per 1'000? »",
    ],
    example_title: "Esempi di richieste",
    disclaimer:
      "Le richieste vocali vengono trascritte automaticamente (OpenAI Whisper) e inviate al nostro ufficio. L'audio non viene conservato dopo l'elaborazione.",
  },
};

function fmtTime(sec) {
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export const VoiceAnfrage = () => {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;

  const [state, setState] = useState("idle"); // idle | recording | transcribing | ready | sending
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState(Array(24).fill(0)); // mic amplitude visualiser
  const [transcript, setTranscript] = useState("");
  const [audioBlob, setAudioBlob] = useState(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const acRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => () => cleanup(), []);

  const cleanup = () => {
    try { recorderRef.current?.state === "recording" && recorderRef.current?.stop(); } catch (_) { /* noop */ }
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    if (timerRef.current) clearInterval(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { acRef.current?.close?.(); } catch (_) { /* noop */ }
    streamRef.current = null;
    recorderRef.current = null;
    acRef.current = null;
    analyserRef.current = null;
  };

  const startRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error(c.err_no_mic);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Prefer webm/opus, fall back to browser default
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setAudioBlob(blob);
        // if user talked less than 3s, warn but still send option
        if (seconds < 3) {
          toast.error(c.err_short);
        }
        // auto-transcribe on stop
        transcribeBlob(blob);
      };
      rec.start();

      // level visualiser
      const AC = window.AudioContext || window.webkitAudioContext;
      const ac = new AC();
      acRef.current = ac;
      const src = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 128;
      analyserRef.current = analyser;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);

      const bars = 24;
      const drawLoop = () => {
        analyser.getByteFrequencyData(data);
        // Sample bars evenly
        const arr = new Array(bars);
        const step = Math.max(1, Math.floor(data.length / bars));
        for (let i = 0; i < bars; i++) arr[i] = data[i * step] / 255;
        setLevels(arr);
        rafRef.current = requestAnimationFrame(drawLoop);
      };
      rafRef.current = requestAnimationFrame(drawLoop);

      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) {
            stopRecording();
            return MAX_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
      setState("recording");
    } catch (err) {
      const isPerm = err?.name === "NotAllowedError" || err?.name === "SecurityError";
      toast.error(isPerm ? c.err_perm : (err?.message || c.err_generic));
      cleanup();
      setState("idle");
    }
  }, [c]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === "recording") {
      try { recorderRef.current.stop(); } catch (_) { /* noop */ }
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    setState("transcribing");
  }, []);

  const transcribeBlob = useCallback(async (blob) => {
    if (!blob) return;
    try {
      const fd = new FormData();
      const ext = (blob.type.includes("webm") ? ".webm" : ".mp3");
      fd.append("file", blob, `voice${ext}`);
      fd.append("lang", lang);
      const res = await axios.post(`${API}/voice-anfrage`, fd, { timeout: 60000 });
      setTranscript(res?.data?.transcript || "");
      setState("ready");
    } catch (err) {
      const detail = err?.response?.data?.detail || c.err_generic;
      toast.error(typeof detail === "string" ? detail : c.err_generic);
      setState("idle");
      setAudioBlob(null);
    }
  }, [lang, c.err_generic]);

  const reset = () => {
    cleanup();
    setState("idle");
    setSeconds(0);
    setTranscript("");
    setAudioBlob(null);
    setLevels(Array(24).fill(0));
  };

  const sendToContact = () => {
    if (!transcript) return;
    setState("sending");
    const message =
      `Voice-Anfrage (transkribiert):\n"${transcript}"\n\n` +
      `Bitte um Rückruf oder schriftliche Antwort mit passendem Verfahren, Machbarkeit und Richtpreis.`;
    try {
      window.dispatchEvent(new CustomEvent("fk:prefill-contact", {
        detail: { message },
      }));
    } catch (_) { /* ignore */ }
    document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
    toast.success(c.toast_sent);
    // keep state so user knows it was sent; a fresh reset button remains available.
    setTimeout(() => setState("ready"), 500);
  };

  const isRec = state === "recording";
  const isTrans = state === "transcribing";
  const isReady = state === "ready";

  return (
    <section
      id="voice"
      data-testid="voice-section"
      className="relative bg-[#0B0B0D] py-24 md:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-white/[0.03] blur-3xl" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        {/* header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 px-2 py-1 border border-white/15 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" />
                {c.badge}
              </span>
              <span>{c.overline}</span>
            </div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[0.95] text-white">
              {c.title_1}
              <br />
              <span className="text-zinc-500">{c.title_2}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
          </div>
        </div>

        <div className="reveal bg-[#121214] border border-white/10 p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* LEFT — Mic + visualiser */}
            <div className="lg:col-span-6 flex flex-col items-center justify-center min-h-[380px]">
              {/* visualiser bars */}
              <div className="h-24 w-full flex items-end justify-center gap-1.5 mb-8" aria-hidden>
                {levels.map((v, i) => (
                  <div
                    key={i}
                    className={`w-2 md:w-2.5 rounded-sm transition-[height] duration-75 ${isRec ? "bg-white" : "bg-white/20"}`}
                    style={{ height: `${8 + v * 88}%` }}
                  />
                ))}
              </div>

              {/* Timer */}
              <div className="mb-6 flex items-center gap-2">
                {isRec && <span className="w-2 h-2 rounded-full bg-red-500 pulse-dot" />}
                <div data-testid="voice-timer" className="font-display tabular-nums text-3xl md:text-4xl text-white">
                  {fmtTime(seconds)}
                  <span className="text-zinc-500 text-lg"> / {fmtTime(MAX_SECONDS)}</span>
                </div>
              </div>

              {/* Big button */}
              {!isRec && !isTrans && !isReady && (
                <button
                  type="button"
                  onClick={startRecording}
                  data-testid="voice-start"
                  className="group relative flex items-center justify-center w-28 h-28 rounded-full bg-white text-black hover:bg-zinc-200 transition-transform duration-300 hover:scale-105"
                  aria-label={c.cta_start}
                >
                  <Mic className="w-10 h-10" strokeWidth={1.5} />
                  <span className="absolute inset-0 rounded-full border-2 border-white/40 animate-ping" />
                </button>
              )}
              {isRec && (
                <button
                  type="button"
                  onClick={stopRecording}
                  data-testid="voice-stop"
                  className="flex items-center justify-center w-28 h-28 rounded-full bg-red-500 text-white hover:bg-red-400 transition-colors duration-200 shadow-[0_0_60px_rgba(239,68,68,0.4)]"
                  aria-label={c.cta_stop}
                >
                  <Square className="w-10 h-10" fill="currentColor" strokeWidth={0} />
                </button>
              )}
              {isTrans && (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-12 h-12 text-white/70 animate-spin" strokeWidth={1.3} />
                  <div className="text-zinc-300 text-sm">{c.transcribing}</div>
                </div>
              )}
              {isReady && (
                <button
                  type="button"
                  onClick={reset}
                  data-testid="voice-reset"
                  className="inline-flex items-center gap-2 border border-white/20 hover:border-white/50 px-4 py-2.5 text-sm text-zinc-200 hover:text-white transition-colors"
                >
                  <RotateCcw className="w-4 h-4" /> {c.cta_reset}
                </button>
              )}

              {/* hints */}
              <div className="mt-6 text-center space-y-1">
                {!isRec && !isTrans && !isReady && (
                  <div className="text-[12px] text-zinc-500">{c.hint_permission}</div>
                )}
                <div className="text-[11px] text-zinc-600">{c.hint_max}</div>
              </div>
            </div>

            {/* RIGHT — Transcript or examples */}
            <div className="lg:col-span-6">
              {isReady && (
                <div>
                  <div className="overline mb-3 flex items-center gap-2">
                    <Waves className="w-3.5 h-3.5" /> {c.transcript_title}
                  </div>
                  <textarea
                    data-testid="voice-transcript"
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    rows={7}
                    className="field text-white leading-relaxed !text-base w-full"
                  />
                  <div className="text-[11px] text-zinc-500 mt-2 mb-5">{c.transcript_edit_hint}</div>
                  <button
                    type="button"
                    onClick={sendToContact}
                    disabled={!transcript.trim() || state === "sending"}
                    data-testid="voice-send"
                    className="inline-flex items-center gap-2 bg-white text-black px-6 py-3.5 text-sm font-semibold disabled:opacity-50 hover:bg-zinc-200 transition-colors shine"
                  >
                    {c.cta_send}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {!isReady && (
                <div>
                  <div className="overline mb-4">{c.example_title}</div>
                  <div className="space-y-3">
                    {c.example_prompts.map((p, i) => (
                      <div key={i} className="border-l border-white/15 pl-4 py-1 text-sm text-zinc-300 leading-relaxed italic">
                        {p}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 text-[11px] text-zinc-500 leading-relaxed max-w-3xl">
            {c.disclaimer}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VoiceAnfrage;
